import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import TagSelectionModal from '../components/TagSelectionModal'
import './Products.css'

const Products = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedTagType, setSelectedTagType] = useState('bike')
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()

  const handleSelectTag = (type) => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    setSelectedTagType(type)
    setIsModalOpen(true)
  }

  const handleGetStarted = () => {
    handleSelectTag('bike')
  }

  const handleEnrollFranchise = () => {
    navigate('/franchise')
  }

  const products = [
    {
      id: 'bike',
      icon: '🏍️',
      title: 'Bike Tag',
      subtitle: 'Compact QR for motorcycles',
      price: '₹499 — ₹699',
      description: 'Perfect for motorcycles and scooters. Compact design that fits seamlessly on your bike. Get instant notifications when someone scans your tag.',
      options: [
        { label: 'Standard Tag', price: '₹499', details: 'Without Accidental Insurance' },
        { label: 'Insurance Plus (1L)', price: '₹599', details: 'Tag + ₹1 Lakh Accidental Insurance' },
        { label: 'Insurance Pro (2L)', price: '₹699', details: 'Tag + ₹2 Lakh Accidental Insurance' }
      ],
      features: [
        'Compact & Weatherproof',
        'Easy Installation',
        'Instant SMS & WhatsApp Alerts',
        'Optional Accidental Insurance',
        'Privacy-First Contact'
      ],
      color: 'var(--primary-color)'
    },
    {
      id: 'car',
      icon: '🚗',
      title: 'Car Tag',
      subtitle: 'Standard QR for cars',
      price: '₹599 — ₹799',
      description: 'Designed for cars and larger vehicles. Durable construction that withstands all weather conditions. Keep your contact information secure and accessible.',
      options: [
        { label: 'Standard Tag', price: '₹599', details: 'Without Accidental Insurance' },
        { label: 'Insurance Pro (3L)', price: '₹799', details: 'Tag + ₹3 Lakh Accidental Insurance' }
      ],
      features: [
        'Durable & Weatherproof',
        'Professional Design',
        'Instant SMS & WhatsApp Alerts',
        'Optional Accidental Insurance',
        'Privacy-First Contact'
      ],
      color: 'var(--primary-color)'
    }
  ]

  return (
    <div className="products-page">
      <div className="hero-background"></div>

      <section className="products-hero">
        <div className="container">
          <div className="hero-content">
            <span className="badge badge-info animate-fadeIn">Our Products</span>
            <h1 className="animate-slideUp stagger-1">
              NAH44 <span className="text-gradient">Products & Services</span>
            </h1>
            <p className="hero-subtitle animate-slideUp stagger-2">
              Vehicle tags, franchise opportunities, and more. 
              Everything you need for secure contact and business growth.
            </p>
          </div>
        </div>
      </section>

      <section className="products-section">
        <div className="container">
          <div className="section-header text-center mb-8">
            <h2 className="animate-slideUp">NAH44 Tags</h2>
            <p className="animate-slideUp stagger-1">
              Secure, privacy-first QR code tags for your vehicles
            </p>
          </div>
          <div className="products-grid">
            {products.map((product, index) => (
              <div 
                key={product.id} 
                className="product-card glass-card animate-slideUp"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="product-icon-wrapper">
                  <div className="product-icon">{product.icon}</div>
                  <div className="product-badge">Best Seller</div>
                </div>
                
                <div className="product-content">
                  <h3>{product.title}</h3>
                  <p className="product-subtitle">{product.subtitle}</p>
                  <p className="product-description">{product.description}</p>
                  
                  <div className="product-options">
                    {product.options.map((opt, idx) => (
                      <div key={idx} className="option-row">
                        <span className="option-label">{opt.label}</span>
                        <span className="option-price">{opt.price}</span>
                        <div className="option-details">{opt.details}</div>
                      </div>
                    ))}
                  </div>

                  <div className="product-features">
                    {product.features.map((feature, idx) => (
                      <div key={idx} className="feature-item">
                        <span className="feature-check">✓</span>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="product-footer">
                    <div className="product-price">
                      <span className="price-label">Price</span>
                      <span className="price-value">{product.price}</span>
                    </div>
                    <button 
                      className="btn btn-primary product-btn"
                      onClick={() => handleSelectTag(product.id)}
                    >
                      Order Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="products-section franchise-section">
        <div className="container">
          <div className="section-header text-center mb-8">
            <h2 className="animate-slideUp">NAH44 Franchise</h2>
            <p className="animate-slideUp stagger-1">
              Become a partner and grow with us
            </p>
          </div>
          <div className="franchise-product-card glass-card">
            <div className="franchise-product-content">
              <div className="franchise-product-icon">🏪</div>
              <h3>Franchise & Tag Distributor Program</h3>
              <p>
                Join the NAH44 network as a Restaurant Franchise or Tag Distributor. Get access to our proven business model, 
                digital platform, and comprehensive support. Manage QR menus, orders, and vehicle tags in your region.
              </p>
              <ul className="franchise-benefits-list">
                <li>✅ Restaurant Franchise – QR menu ordering, table management, KDS</li>
                <li>✅ Tag Distributor – Sell NAH44 vehicle tags in your area</li>
                <li>✅ Training, marketing materials, and ongoing support</li>
              </ul>
              <button className="btn btn-primary btn-large" onClick={handleEnrollFranchise}>
                Enroll Now
              </button>
              <Link to="/franchise" className="btn btn-secondary mt-3" style={{ display: 'inline-block', marginTop: '0.75rem' }}>
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="products-benefits">
        <div className="container">
          <div className="section-header text-center mb-8">
            <h2 className="animate-slideUp">Why Choose NAH44 Tags?</h2>
            <p className="animate-slideUp stagger-1">
              Premium features designed for your peace of mind
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card glass-card">
              <div className="benefit-icon">🛡️</div>
              <h3>Privacy Protected</h3>
              <p>Your phone number stays hidden. We securely bridge the connection.</p>
            </div>
            <div className="benefit-card glass-card">
              <div className="benefit-icon">⚡</div>
              <h3>Instant Alerts</h3>
              <p>Get notified immediately via SMS and WhatsApp when someone scans your tag.</p>
            </div>
            <div className="benefit-card glass-card">
              <div className="benefit-icon">🌐</div>
              <h3>Works Offline</h3>
              <p>NFC technology ensures your tag works even without internet connection.</p>
            </div>
            <div className="benefit-card glass-card">
              <div className="benefit-icon">🔒</div>
              <h3>Secure & Reliable</h3>
              <p>Industry-standard security with encrypted data transmission.</p>
            </div>
            <div className="benefit-card glass-card">
              <div className="benefit-icon">📱</div>
              <h3>Easy Management</h3>
              <p>Manage all your tags from one dashboard. Track scans and contacts.</p>
            </div>
            <div className="benefit-card glass-card">
              <div className="benefit-icon">🚚</div>
              <h3>Fast Delivery</h3>
              <p>Quick shipping across India. Get your tag delivered to your doorstep.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="products-cta">
        <div className="container">
          <div className="cta-card glass-card">
            <h2>Ready to Get Started?</h2>
            <p>Join thousands of vehicle owners who trust NAH44 for secure contact management.</p>
            <button className="btn btn-primary btn-large" onClick={handleGetStarted}>
              Get Your Tag Now
            </button>
          </div>
        </div>
      </section>

      <TagSelectionModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        initialTagType={selectedTagType}
      />
    </div>
  )
}

export default Products

